import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";

import type { Graphics as GraphicsType, PointData } from "pixi.js";
import { Graphics, Texture } from "pixi.js";
import { extend, useTick } from "@pixi/react";

extend({ Graphics });

interface PixBimSelectProps {
  fill?: string;
}
export interface PixBimSelectType {
  setStart: (point: PointData | false) => void;
  setPos: (point: PointData | false) => void;
}
const getBox = (start: PointData | false, current: PointData | false) => {
  if (!start || !current) {
    return;
  }
  var { x, y } = start;
  var width = current.x - start.x;
  var height = current.y - start.y;
  if (width < 0) {
    x = x + width;
    width = -width;
  }
  if (height < 0) {
    y = y + height;
    height = -height;
  }
  return { x, y, width, height };
};

const PixBimSelect = forwardRef<PixBimSelectType, PixBimSelectProps>(
  ({ fill = "rgba(173, 216, 230, 0.5)" }, ref) => {
    const [fillColor] = useState(fill);
    const strokeColor = "rgba(173, 216, 230, 0.9)";
    const strokeWidth = 10;
    const grapRef = useRef<Graphics | null>(null);
    var start: PointData | false = false;
    var current: PointData | false = false;
    useImperativeHandle(ref, () => {
      return {
        setStart: (point: PointData | false) => {
          start = point;
        },
        setPos: (point: PointData | false) => {
          current = point;
          return getBox(start, current);
        },
      };
    });
    useTick(() => {
      if (!grapRef.current) {
        return;
      }
      const g = grapRef.current;
      const box = getBox(start, current) || { x: 0, y: 0, width: 0, height: 0 };
      const { x, y, width, height } = box;
      const texture: Texture = Texture.WHITE;
      texture.source.scaleMode = "nearest";
      g.clear();
      g.fill({ texture, color: fillColor, alpha: 0.5 });
      g.rect(x, y, width, height);
      g.eventMode = "none";
      g.stroke({ color: strokeColor, width: strokeWidth, texture });
      g.fill();
    });

    const draw = useCallback(
      (g: GraphicsType) => {
        const box = getBox(start, current) || { x: 0, y: 0, width: 0, height: 0 };
        const { x, y, width, height } = box;
        const texture: Texture = Texture.WHITE;
        texture.source.scaleMode = "nearest";
        g.clear();
        g.fill({ texture, color: fillColor });
        g.rect(x, y, width, height);
        g.eventMode = "none";
        g.stroke({ color: strokeColor, width: strokeWidth, texture });
        g.fill();
      },
      [start, current, strokeColor, fillColor, strokeWidth]
    );

    return <graphics draw={draw} ref={grapRef} />;
  }
);

export default PixBimSelect;
